import { cloneDeep, find, includes, isEmpty, isEqual, isPlainObject } from 'es-toolkit/compat';

export const overlayAppearanceTypes = [
  { id: 'spotDetections', name: 'Spot detections', group: 'Point overlays', kind: 'points' },
  { id: 'cellClassification', name: 'Cell classification', group: 'Point overlays', kind: 'points' },
  { id: 'scores', name: 'Scores', group: 'Point overlays', kind: 'points' },
  { id: 'spotPositivity', name: 'Spot positivity', group: 'Point overlays', kind: 'points' },
  { id: 'markerPositivity', name: 'Marker positivity', group: 'Point overlays', kind: 'points' },
  { id: 'markerProbability', name: 'Marker probability', group: 'Point overlays', kind: 'points' },
  { id: 'quantification', name: 'Quantification', group: 'Point overlays', kind: 'points' },
  { id: 'clusters', name: 'Cell clusters', group: 'Point overlays', kind: 'points' },
  { id: 'pointAnnotations', name: 'Point annotations', group: 'Annotations', kind: 'points' },
  { id: 'polygonAnnotations', name: 'Polygon annotations', group: 'Annotations', kind: 'geometry' },
  { id: 'cellSegmentation', name: 'Cell segmentation', group: 'Geometry overlays', kind: 'geometry' },
  { id: 'nuclearSegmentation', name: 'Nuclear segmentation', group: 'Geometry overlays', kind: 'geometry' },
  { id: 'tissueRegions', name: 'Tissue regions', group: 'Geometry overlays', kind: 'geometry' },
  { id: 'tmaCores', name: 'TMA cores', group: 'Geometry overlays', kind: 'geometry' },
  { id: 'cellDensity', name: 'Cell density', group: 'Maps', kind: 'heatmap' },
  { id: 'rasterHeatmaps', name: 'Raster heatmaps', group: 'Maps', kind: 'heatmap' },
] as const;

export type OverlayAppearanceType = (typeof overlayAppearanceTypes)[number]['id'];
export type ZoomBandRadii = [number, number, number, number];
export type ZoomBandOutlineWidths = [number, number, number, number];
export interface PointOverlayAppearance {
  kind: 'points';
  shape: 'circle' | 'square' | 'diamond';
  radiiMicronsByZoomBand: ZoomBandRadii;
  style: 'filled' | 'outline';
  opacity: number;
  outlineWidthPx: number | ZoomBandOutlineWidths;
}
export interface GeometryOverlayAppearance {
  kind: 'geometry';
  mode: 'outline' | 'fill';
  opacity: number;
  outlineWidthPx: number | ZoomBandOutlineWidths;
}
export interface HeatmapOverlayAppearance {
  kind: 'heatmap';
  opacity: number;
}
export type OverlayAppearance = PointOverlayAppearance | GeometryOverlayAppearance | HeatmapOverlayAppearance;
export interface OverlayAppearancePreferences {
  version: 1;
  overlays: Partial<Record<OverlayAppearanceType, OverlayAppearance>>;
}

export const overlayZoomBands = [
  { name: 'Close-up', range: 'Zoom >= -1', minZoom: -1 },
  { name: 'Detail', range: '-1.5 <= zoom < -1', maxZoom: -1, minZoom: -1.5 },
  { name: 'Wide', range: '-2 <= zoom < -1.5', maxZoom: -1.5, minZoom: -2 },
  { name: 'Overview', range: 'Zoom < -2', maxZoom: -2 },
] as const;

export const getOverlayOutlineWidths = (
  appearance: PointOverlayAppearance | GeometryOverlayAppearance
): ZoomBandOutlineWidths => {
  const width = appearance.outlineWidthPx;
  return typeof width === 'number' ? [width, width, width, width] : width;
};

export const getOutlineWidthAtZoom = (widths: ZoomBandOutlineWidths, zoom: number): number => {
  const bandIndex = overlayZoomBands.findIndex(
    (band) => (!('minZoom' in band) || zoom >= band.minZoom) && (!('maxZoom' in band) || zoom <= band.maxZoom)
  );
  return widths[bandIndex < 0 ? 0 : bandIndex];
};

export const getDefaultOverlayAppearance = (type: OverlayAppearanceType, multiplex = false): OverlayAppearance => {
  const definition = find(overlayAppearanceTypes, { id: type });
  if (definition.kind === 'heatmap') return { kind: 'heatmap', opacity: 75 };
  if (definition.kind === 'geometry') {
    return { kind: 'geometry', mode: type === 'tissueRegions' ? 'fill' : 'outline', opacity: 100, outlineWidthPx: 2 };
  }
  const radiiMicronsByZoomBand: ZoomBandRadii =
    type === 'spotDetections'
      ? [1, 1.25, 1.5, 1.75]
      : type === 'spotPositivity'
      ? [1.25, 1.5, 1.75, 2]
      : includes(['markerPositivity', 'markerProbability', 'quantification'], type)
      ? [2.5, 3, 3.5, 4]
      : [2, 2.5, 3, 3.5];
  return {
    kind: 'points',
    shape: 'circle',
    radiiMicronsByZoomBand,
    style: type === 'markerPositivity' && multiplex ? 'outline' : 'filled',
    opacity: 100,
    outlineWidthPx: 1.5,
  };
};

const isSteppedNumber = (value: unknown, min: number, max: number, step: number): value is number =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  value >= min &&
  value <= max &&
  Math.abs((value - min) / step - Math.round((value - min) / step)) < 0.000001;

export const validateOverlayAppearance = (type: OverlayAppearanceType, appearance: unknown): Record<string, string> => {
  const definition = find(overlayAppearanceTypes, { id: type });
  if (!definition || !isPlainObject(appearance)) return { kind: 'Invalid overlay appearance.' };
  const value = appearance as OverlayAppearance;
  const errors: Record<string, string> = {};
  if (value.kind !== definition.kind) return { kind: 'Appearance does not match this overlay type.' };
  if (!isSteppedNumber(value.opacity, 0, 100, 1)) errors.opacity = 'Enter a whole percentage from 0 to 100.';
  if (value.kind === 'points') {
    if (!includes(['circle', 'square', 'diamond'], value.shape)) errors.shape = 'Choose a marker shape.';
    if (!includes(['filled', 'outline'], value.style)) errors.style = 'Choose Filled or Outline.';
    if (!Array.isArray(value.radiiMicronsByZoomBand) || value.radiiMicronsByZoomBand.length !== 4) {
      errors.radiiMicronsByZoomBand = 'Set a radius for all four zoom bands.';
    } else {
      value.radiiMicronsByZoomBand.forEach((radius, index) => {
        if (!isSteppedNumber(radius, 0.05, 20, 0.05)) errors[`radius-${index}`] = 'Enter 0.05 to 20 in steps of 0.05.';
      });
    }
  }
  if (value.kind === 'geometry' && !includes(['outline', 'fill'], value.mode)) errors.mode = 'Choose Outline or Fill.';
  if (value.kind !== 'heatmap') {
    if (Array.isArray(value.outlineWidthPx)) {
      if (value.outlineWidthPx.length !== overlayZoomBands.length) {
        errors.outlineWidthPx = 'Set an outline width for all four zoom bands.';
      }
      value.outlineWidthPx.forEach((width, index) => {
        if (!isSteppedNumber(width, 0.5, 5, 0.5)) {
          errors[`outlineWidth-${index}`] = 'Enter 0.5 to 5 pixels in steps of 0.5.';
        }
      });
    } else if (!isSteppedNumber(value.outlineWidthPx, 0.5, 5, 0.5)) {
      errors.outlineWidthPx = 'Enter 0.5 to 5 pixels in steps of 0.5.';
    }
  }
  return errors;
};

export const parseOverlayAppearancePreferences = (value: unknown): OverlayAppearancePreferences => {
  if (value === undefined || value === null) return { version: 1, overlays: {} };
  if (!isPlainObject(value)) throw new Error('Overlay appearance preferences are invalid.');
  const preferences = value as OverlayAppearancePreferences;
  if (preferences.version !== 1 || !isPlainObject(preferences.overlays)) {
    throw new Error('Overlay appearance preferences have an unsupported format.');
  }
  for (const [type, appearance] of Object.entries(preferences.overlays)) {
    if (!isEmpty(validateOverlayAppearance(type as OverlayAppearanceType, appearance))) {
      throw new Error('Saved overlay appearance contains invalid settings.');
    }
  }
  return cloneDeep(preferences);
};

export const mergeOverlayAppearanceDraft = (
  latest: OverlayAppearancePreferences,
  baseline: OverlayAppearancePreferences,
  draft: OverlayAppearancePreferences
): OverlayAppearancePreferences => {
  const overlays = { ...latest.overlays };
  const types = new Set([...Object.keys(baseline.overlays), ...Object.keys(draft.overlays)]);
  types.forEach((type) => {
    const key = type as OverlayAppearanceType;
    if (isEqual(baseline.overlays[key], draft.overlays[key])) return;
    if (draft.overlays[key] === undefined) delete overlays[key];
    else overlays[key] = draft.overlays[key];
  });
  return { version: 1, overlays };
};
